import { Component, Inject, PLATFORM_ID, OnInit, ApplicationRef, ViewChild } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { Icons } from '../../../shared/helpers/icons';
import { LucideAngularModule } from 'lucide-angular';
import { ISchedule } from '../../../shared/models/schedule';
import { ScheduleService } from '../../../shared/services/schedule.service';
import { UserDataService } from '../../../shared/services/userData.service';
import { EventDetailsComponent } from "./event-details/event-details.component";
import { EventEditComponent } from "./event-edit/event-edit.component";
import { NewEventComponent } from "./new-event/new-event.component";
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { DeleteConfirmationComponent } from "../../components/delete-confirmation/delete-confirmation.component";
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { ESectionTypes } from '../../../shared/models/eop_types.enum';
import { ScheduleTableComponent } from './schedule-table/schedule-table.component';
import { Subject, takeUntil } from 'rxjs';
import { EopNavigationService } from '../../../shared/services/eopNavigation.service';
import { FilesService } from '../../../shared/services/files.service';
import { DateUtils } from '../../../shared/helpers/dateUtils';
import { IUserInfo } from '../../../shared/models/contact';


@Component({
  selector: 'schedule',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    PrvNextButtonsComponent,
    EopAssistantComponent,
    FullCalendarModule,
    EventDetailsComponent,
    EventEditComponent,
    NewEventComponent,
    SpinnerComponent,
    PopupMessageComponent,
    DeleteConfirmationComponent,
    IntroductionComponent,
    ScheduleTableComponent
  ],
  templateUrl: './schedule.component.html',
  styleUrl: './schedule.component.css'
})
export class ScheduleComponent implements OnInit {
  
  @ViewChild('fullcalendar') calendarComponent!: FullCalendarComponent;
  private destroy$ = new Subject<void>();

  sectionTypeId = ESectionTypes.Schedule;

  icons = Icons;
  selectedEvent: ISchedule | null = null;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  schedulesList: any;
  scheduleTable: ISchedule[] = [];
  selectedDate: string | null = null;
  messageModal: string | null = null;
  popupMessage: string = '';
  schoolYears: string[] = [];
  selectedSchoolYear: string = '';
  deleteItemType: string = '';

  showIntro: boolean = false;
  showEventDetails: boolean = false;
  showEventEdit: boolean = false;
  showDeleteConfirmation: boolean = false;
  showCreateEvent: boolean = false;
  loading: boolean = false;

  calendarOptions: CalendarOptions = {
    plugins: [],
    initialView: 'dayGridMonth',
    events: [] // set it to an empty array initially to prevent hydration issues
  };
  hydrated = false // set it to false initially to prevent hydration issues, only populate it after the schedules are loaded and the calendar options are initialized
   
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private userDataService: UserDataService,
    private scheduleService: ScheduleService,
    private appRef: ApplicationRef,
    public eopNavigationService: EopNavigationService,
    private filesService: FilesService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.schoolYears = DateUtils.getSchoolYears(6);
    this.selectedSchoolYear = DateUtils.getCurrentSchoolYear();
    this.loadSchedules();   
  }
  
  loadSchedules(): void {
    if (this.currentBuildingId && this.selectedSchoolYear) {
      this.loading = true;
      this.scheduleService.getSchedules(this.currentBuildingId, this.selectedSchoolYear).subscribe({
        next: (response) => {
          this.loading = false;
          console.log('Fetched schedules list:', response);
          if (!response.has_error) {
            // Store the response data in the scheduleTable property to populate the schedule left table
            this.scheduleTable = response.data ? response.data : [];
            // Map the response data to the format required by FullCalendar to populate the right calendar
            this.schedulesList = response.data?.map((schedule: ISchedule) => ({
              id: schedule.id,                       
              title: schedule.name,                   
              start: schedule.start_date,             
              end: schedule.end_date,                 
              extendedProps: {
                time: schedule.time,                  
                description: schedule.details,
                type: schedule.type_name,
                duration: schedule.duration,
                date_created: schedule.last_updated_at,
                last_updated_user_name: schedule.last_updated_user_name,
                buildingId: schedule.building_id,
                type_id: schedule.type_id,
                files: schedule.files || []
              }
            }));
            if (this.hydrated) {
              this.refreshCalendarEvents();
            } else if (isPlatformBrowser(this.platformId)) {
              this.appRef.isStable
                .pipe(takeUntil(this.destroy$))
                .subscribe((isStable) => {
                  if (isStable) {
                    this.hydrated = true;
                    this.initializeCalendarOptions(); // first time init
                  }
                });
            }
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error fetching schedulesList:', error.message);
        }
      });
    }
  };
  
  // This method initializes the calendar options for FullCalendar
  initializeCalendarOptions(): void {
    this.calendarOptions = {
      plugins: [ dayGridPlugin, interactionPlugin ],
      initialView: 'dayGridMonth',
      editable: false, // Disable drag-and-drop
      eventResizableFromStart: false, // Disable resizing events
      dayMaxEventRows: 3,
      events: this.schedulesList,
      dateClick: (arg) => { // when click on a date on the calendar to pop up the create new event form
        if (this.canSave()) {
          this.selectedDate = arg.dateStr; // set the selected date to the clicked date
          this.handleOpenModal('create-event');
        }
      },
      eventClick: (info) => { // when click on an event on the calendar to pop up the details modal
        const eventDetails = {
          id: parseInt(info.event.id, 10),
          start_date: info.event.start,
          end_date: info.event.end ?? info.event.start,
          name: info.event.title,
          details: info.event.extendedProps['description'],
          time: info.event.extendedProps['time'],
          duration: info.event.extendedProps['duration'],
          last_updated_user_name: info.event.extendedProps['last_updated_user_name'],
          building_id: info.event.extendedProps['buildingId'],
          type_id: info.event.extendedProps['type_id'],
          last_updated_at: info.event.extendedProps['date_created'],
          type_name: info.event.extendedProps['type'],
          files: info.event.extendedProps['files'] || []
        };
        this.handleOpenModal('event-details', eventDetails);
      },
    };
  }

  // update the calendar options when the schedules are loaded
  updateCalendarOptions(): void {
    if (this.schedulesList && this.schedulesList.length > 0) {
      this.calendarOptions.events = this.schedulesList;
    } else {
      this.calendarOptions.events = []; // set to empty array if no schedules are available
    }
  }

  handleSchoolYearChange(event: string): void {
    this.selectedSchoolYear = event;
    this.loadSchedules(); // Reload schedules when school year changes
  }

  handleOpenModal(modalName: string, selectedItem?: any, itemType?: string): void {
    if (modalName === 'intro') {
      this.showIntro = true;
    } else if (modalName === 'edit-event') {
      this.showEventDetails = false;
      this.showEventEdit = true;
    } else if (modalName === 'create-event') {
      if (!this.selectedDate) { // if no date is selected, default to today and pass it to the modal
        this.selectedDate = new Date().toISOString().split('T')[0];
      }
      this.showCreateEvent = true;
    } else if (modalName === 'event-details') {
      this.showEventDetails = true;
    } else if (modalName === 'delete-confirmation') {
      this.showEventDetails = false;
      this.showDeleteConfirmation = true;
    }
    this.selectedEvent = selectedItem || null;
    this.deleteItemType = itemType || '';
  }

  handleCloseModal(modalName: string): void {
    if (modalName === 'intro') {
      this.showIntro = false;
    } else if (modalName === 'edit-event') {
      this.showEventEdit = false;
    } else if (modalName === 'create-event') {
      this.showCreateEvent = false;
    } else if (modalName === 'event-details') {
      this.showEventDetails = false;
    } else if (modalName === 'delete-confirmation') {
      this.showDeleteConfirmation = false;
    }

    this.selectedDate = null; // Reset selected date when closing modals
    this.selectedEvent = null; // Reset selected event when closing modals
  }

  handleSave(message: {type: string, message: string}, actionType: string): void {
    this.handleCloseModal(actionType);
    this.loadSchedules();
    this.updateCalendarOptions(); // Update calendar options after saving changes

    if (message.type === 'success') {
      // this.loadAssessmentsCases();
      this.messageModal = 'success';
      this.popupMessage = message.message;
      
    } else if (message.type === 'unsuccess') {
      this.messageModal = 'unsuccess';
      this.popupMessage = message.message;
    } 
  }

  handleDelete(item: any): void {
    if (item.id) {
      this.loading = true;
      // handle deletion based on the item type
      // this case is for deleting a calendar event
      if (this.deleteItemType === 'schedule') {
        this.scheduleService.deleteSchedule(String(item.id)).subscribe({
          next: (response) => {
            this.loading = false;
            if (!response.has_error) {
              this.messageModal = "success";
              this.popupMessage = 'This occurred after deleting an event.';
              this.handleCloseModal('delete-confirmation');
              this.loadSchedules();
            }
          },
          error: (error) => {
            this.loading = false;
            this.messageModal = "unsuccess";
            this.popupMessage = error.message;
            console.error('Error deleting the meeting:', error.message);
          }
        })
      } else if (this.deleteItemType === 'uploaded file') { // this case is for deleting an uploaded file from an event
        console.log('Deleting file:', item);
        this.filesService.deleteFile(item.building_id, item.section_id, item.id).subscribe({
          next: (response) => {
            this.loading = false;
            if (!response.has_error) {
              this.messageModal = "success";
              this.popupMessage = 'This occurred after deleting a file.';
              this.handleCloseModal('delete-confirmation');
              this.loadSchedules();
            }
          },
          error: (error) => {
            this.loading = false;
            this.messageModal = "unsuccess";
            this.popupMessage = error.message;
            console.error('Error deleting the file:', error.message);
          } 
        })
      } 
      this.deleteItemType = '';
    }
  }

  refreshCalendarEvents() {
    const calendarApi = this.calendarComponent?.getApi();
    if (calendarApi) {
      calendarApi.removeAllEvents(); // clear current events
      this.schedulesList.forEach((event: EventInput) => {
        calendarApi.addEvent(event); // add updated events
      });
    }
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
  
  ngOnDestroy() { // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }
}
